import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { API, CullCategoryDTO } from '../../../services/api/api';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { PermissionService } from '../../../services/permission.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit';

@Component({
  selector: 'sb-page-cull-categories',
  templateUrl: './cull-categories.component.html',
  styleUrls: ['./cull-categories.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CullCategoriesComponent extends RequestClass implements ModalNavigationComponent {
  public readonly modal = inject(ModalNavigationService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() isInIntegratedContext: boolean;

  @Output() selectCategory: EventEmitter<CullCategoryDTO> = new EventEmitter<CullCategoryDTO>();

  // global variables
  isInModalContext: boolean;
  showSideBar: boolean = false;

  // table wrapper variables
  filterCategories: CullCategoryDTO[] = [];
  selectedCullCategory: CullCategoryDTO;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    const sub = this.api
      .ca_md_CullCategory_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (categories: CullCategoryDTO[]) => {
          this.filterCategories = this.buildSplitButtons(categories);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  addCullCategory() {
    this.selectedCullCategory = new CullCategoryDTO();
    this.toggleSideBar();
  }

  editCullCategory(assetCategory: CullCategoryDTO) {
    this.selectedCullCategory = assetCategory;
    this.toggleSideBar();
  }

  selectCullCategory(cullCategory: CullCategoryDTO) {
    if (this.isInModalContext) {
      this.modal.result(cullCategory);
    }
  }

  updateCullCategory(cullCategory: CullCategoryDTO) {
    if (!cullCategory.id) {
      this.api
        .ca_md_CullCategory_createCategory(cullCategory)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(() => {
          this.resetView();
        });
    } else {
      this.api
        .ca_md_CullCategory_editCategory(cullCategory)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(() => {
          this.resetView();
        });
    }
  }

  resetView() {
    this.toggleSideBar();
    this.selectedCullCategory = null;
    this.requestDebounced();
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_category_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          action: 'edit',
          tooltip: 'lang.globals.details',
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_list])) {
        item.sbButtons.push({
          icon: 'list',
          tooltip: 'lang.globals.cullCategories',
          link: RoutingID.filters,
          queryParams: { cullCategories: item?.id },
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(cullCategory: CullCategoryDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editCullCategory(cullCategory);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
